import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { FileText, Save, Eye, Send, RotateCcw, X, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "./useApi";

export function EmailTemplatesCard({ token, t, hasApiKey }) {
  var [emailTemplates, setEmailTemplates] = useState({ categories: {} });
  var [selectedTemplate, setSelectedTemplate] = useState(null);
  var [editingTemplate, setEditingTemplate] = useState(null);
  var [previewHtml, setPreviewHtml] = useState(null);
  var [savingTemplate, setSavingTemplate] = useState(false);
  var [sendingTestEmail, setSendingTestEmail] = useState(false);
  var { fetchJson, fetchApi, putJson, postJson } = useApi(token);

  useEffect(function() { fetchTemplates(); }, []);

  async function fetchTemplates() {
    try { var data = await fetchJson("/api/admin/email-templates"); setEmailTemplates(data); } catch (err) {}
  }

  async function handleSelectTemplate(templateId) {
    try {
      var data = await fetchJson("/api/admin/email-templates/" + templateId);
      setSelectedTemplate(data);
      setEditingTemplate({ subject: data.subject, html_body: data.html_body, name: data.name, description: data.description });
      setPreviewHtml(null);
    } catch (err) { toast.error(t("common.error")); }
  }

  async function handleSaveTemplate() {
    if (!selectedTemplate) return;
    setSavingTemplate(true);
    try {
      var response = await putJson("/api/admin/email-templates/" + selectedTemplate.template_id, editingTemplate);
      if (response.ok) { toast.success(t("settings.templateSaved")); fetchTemplates(); var data = await response.json(); setSelectedTemplate(data); }
      else { var error = await response.json(); toast.error(error.detail || t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
    finally { setSavingTemplate(false); }
  }

  async function handleResetTemplate() {
    if (!selectedTemplate) return;
    try {
      var response = await fetchApi("/api/admin/email-templates/" + selectedTemplate.template_id + "/reset", { method: "POST" });
      if (response.ok) {
        toast.success(t("settings.templateReset")); fetchTemplates();
        var data = await response.json(); setSelectedTemplate(data);
        setEditingTemplate({ subject: data.subject, html_body: data.html_body, name: data.name, description: data.description });
        setPreviewHtml(null);
      }
    } catch (err) { toast.error(t("common.error")); }
  }

  async function handlePreviewTemplate() {
    if (!selectedTemplate) return;
    try {
      var response = await fetchApi("/api/admin/email-templates/" + selectedTemplate.template_id + "/preview", { method: "POST" });
      if (response.ok) { var data = await response.json(); setPreviewHtml(data.html_body); }
    } catch (err) { toast.error(t("common.error")); }
  }

  async function handleSendTestEmail() {
    if (!selectedTemplate || !editingTemplate) return;
    setSendingTestEmail(true);
    try {
      var response = await postJson("/api/admin/email-templates/" + selectedTemplate.template_id + "/send-test", { subject: editingTemplate.subject, html_body: editingTemplate.html_body });
      if (response.ok) { var data = await response.json(); toast.success(t("settings.testEmailSentTo", { email: data.sent_to })); }
      else { var error = await response.json(); toast.error(error.detail || t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
    finally { setSendingTestEmail(false); }
  }

  return (
    <Card className="bento-card border-purple-500/50" data-testid="email-templates-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-purple-500" />{t("settings.emailTemplatesTitle")}</CardTitle>
        <CardDescription>{t("settings.emailTemplatesDesc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {Object.entries(emailTemplates.categories || {}).map(function([categoryKey, category]) {
          return (
            <div key={categoryKey} className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">{category.name}</h4>
              <div className="grid gap-2">
                {(category.templates || []).map(function(template) {
                  var isSelected = selectedTemplate?.template_id === template.template_id;
                  return (
                    <div key={template.template_id} className={"p-3 rounded-lg border cursor-pointer transition-colors " + (isSelected ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20" : "border-border hover:border-purple-300 hover:bg-muted/30")} onClick={function() { handleSelectTemplate(template.template_id); }}>
                      <div className="flex items-center justify-between">
                        <div><p className="font-medium text-sm">{template.name}</p><p className="text-xs text-muted-foreground">{template.description}</p></div>
                        <div className="flex items-center gap-2">
                          {template.is_customized && <span className="text-xs px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300">{t("settings.customized")}</span>}
                          {isSelected ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {selectedTemplate && editingTemplate && (
          <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">{t("settings.editTemplate")}: {selectedTemplate.name}</h4>
              <Button variant="ghost" size="sm" onClick={function() { setSelectedTemplate(null); setEditingTemplate(null); setPreviewHtml(null); }}><X className="w-4 h-4" /></Button>
            </div>
            <div>
              <Label className="form-label">{t("settings.emailSubject")}</Label>
              <Input value={editingTemplate.subject} onChange={function(e) { setEditingTemplate({ ...editingTemplate, subject: e.target.value }); }} />
            </div>
            <div>
              <Label className="form-label">{t("settings.emailBody")} (HTML)</Label>
              <textarea className="w-full min-h-[200px] p-3 border rounded-lg bg-background text-foreground font-mono text-sm resize-y" value={editingTemplate.html_body} onChange={function(e) { setEditingTemplate({ ...editingTemplate, html_body: e.target.value }); }} />
            </div>
            <div>
              <Label className="form-label">{t("settings.availableVariables")}</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {(selectedTemplate.variables || []).map(function(variable) { return <code key={variable} className="text-xs px-2 py-1 rounded bg-muted">{"{{" + variable + "}}"}</code>; })}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleSaveTemplate} disabled={savingTemplate}>{savingTemplate ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{t("settings.saveTemplate")}</Button>
              <Button variant="outline" onClick={handlePreviewTemplate}><Eye className="w-4 h-4 mr-2" />{t("settings.previewTemplate")}</Button>
              <Button variant="outline" onClick={handleSendTestEmail} disabled={sendingTestEmail || !hasApiKey} className="border-green-500 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20">
                {sendingTestEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}{t("settings.sendTestEmail")}
              </Button>
              {selectedTemplate.is_customized && <Button variant="outline" onClick={handleResetTemplate}><RotateCcw className="w-4 h-4 mr-2" />{t("settings.resetToDefault")}</Button>}
            </div>
            {!hasApiKey && <p className="text-sm text-muted-foreground">{t("settings.configureEmailFirst")}</p>}
            {previewHtml && (
              <div className="space-y-2">
                <Label className="form-label">{t("settings.emailPreview")}</Label>
                <div className="p-4 border rounded-lg bg-white dark:bg-gray-900 max-h-[400px] overflow-auto" dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
